import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import PHAddressSelect from '../components/PHAddressSelect';
import LocationPicker from '../components/LocationPicker';
import Footer from '../components/Footer';
import debounce from 'lodash/debounce';
import { PAYMENT_ICONS } from '../constants/paymentIcons';
import DeleteAccountModal from '../components/settings/DeleteAccountModal';
import { useTranslation } from 'react-i18next';
import { setAppLanguage } from '../i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AccountSettings = ({ user, onLogout, onUpdate }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('personal');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [exportingData, setExportingData] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [privacyEmail, setPrivacyEmail] = useState('');
  const [error, setError] = useState('');

  // Personal Information
  const [personalInfo, setPersonalInfo] = useState({
    name: user?.name || '',
    username: user?.username || '',
    email: user?.email || '',
    phone: user?.phone || '',
    location: user?.location || '',
    bio: user?.bio || '',
    skills: user?.skills?.join(', ') || ''
  });
  
  // Username validation state
  const [usernameStatus, setUsernameStatus] = useState({ checking: false, available: null, message: '' });

  // Avatar upload
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar ? `${BACKEND_URL}${user.avatar}` : null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Security Settings
  const [securitySettings, setSecuritySettings] = useState(null);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Privacy Settings
  const [privacySettings, setPrivacySettings] = useState(null);

  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState(null);

  // Tax Information
  const [taxInfo, setTaxInfo] = useState({
    tax_id_type: 'TIN',
    tax_id_number: '',
    business_name: '',
    business_address: '',
    tax_classification: 'individual',
    vat_registered: false
  });

  // Payment Methods
  const [paymentMethods, setPaymentMethods] = useState([]);

  // Language & Currency
  const [languageSettings, setLanguageSettings] = useState({
    language: 'en',
    timezone: 'Asia/Manila'
  });
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(user?.currency || 'USD');

  // Offered Services
  const [offeredServices, setOfferedServices] = useState([]);

  // Credentials
  const [credentials, setCredentials] = useState([]);
  const [uploadingCredential, setUploadingCredential] = useState(false);

  // OTP config (from owner panel)
  const [otpGlobalEnabled, setOtpGlobalEnabled] = useState(false);
  const [userOtpEnabled, setUserOtpEnabled] = useState(false);

  // Password visibility toggles
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  // Saved Locations
  const [savedLocations, setSavedLocations] = useState([]);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [newLocation, setNewLocation] = useState({
    label: '',
    full_address: '',
    street: '',
    barangay: '',
    city: '',
    province: '',
    country: 'Philippines',
    coordinates: null,
    is_default: false
  });

  useEffect(() => {
    fetchAllSettings();
    fetchSavedLocations();
    fetchCredentials();
    fetchOtpConfig();
    axios.get(`${API}/owner/company-details`).then(res => {
      setPrivacyEmail(res.data?.privacy_email || '');
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSavedLocations = async () => {
    try {
      const response = await axios.get(`${API}/user/locations`);
      setSavedLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchCredentials = async () => {
    try {
      const response = await axios.get(`${API}/credentials/my`);
      setCredentials(response.data);
    } catch (error) {
      console.error('Error fetching credentials:', error);
    }
  };

  const handleUploadCredential = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCredential(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('credential_type', type);
      await axios.post(`${API}/upload/credential`, formData);
      fetchCredentials();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to upload credential');
    } finally {
      setUploadingCredential(false);
    }
  };

  const handleDeleteCredential = async (credId) => {
    if (!window.confirm(t('settings.confirmDeleteCredential'))) return;
    try {
      await axios.delete(`${API}/credentials/${credId}`);
      fetchCredentials();
    } catch (error) {
      alert('Failed to delete credential');
    }
  };

  const fetchOtpConfig = async () => {
    try {
      const res = await axios.get(`${API}/otp/status`);
      setOtpGlobalEnabled(res.data?.enabled || false);
    } catch {
      setOtpGlobalEnabled(false);
    }
    try {
      const res = await axios.get(`${API}/settings`);
      // OTP login defaults to ON — treat missing/undefined as enabled.
      setUserOtpEnabled(res.data?.otp_enabled !== false);
    } catch {}
  };

  const toggleUserOtp = async (enabled) => {
    try {
      await axios.put(`${API}/security-settings/otp?enabled=${enabled}`);
      setUserOtpEnabled(enabled);
    } catch (error) {
      alert('Failed to update OTP preference');
    }
  };

  const handleSaveLocation = async () => {
    try {
      if (editingLocation) {
        await axios.put(`${API}/user/locations/${editingLocation.id}`, newLocation);
      } else {
        await axios.post(`${API}/user/locations`, newLocation);
      }
      fetchSavedLocations();
      setShowLocationModal(false);
      setEditingLocation(null);
      setNewLocation({
        label: '',
        full_address: '',
        street: '',
        barangay: '',
        city: '',
        province: '',
        country: 'Philippines',
        coordinates: null,
        is_default: false
      });
      setSuccess('Location saved successfully');
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save location');
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm(t('settings.confirmDeleteLocation'))) return;
    try {
      await axios.delete(`${API}/user/locations/${locationId}`);
      fetchSavedLocations();
      setSuccess('Location deleted');
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to delete location');
    }
  };

  // Username availability check
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkUsernameAvailability = useCallback(
    debounce(async (username) => {
      if (!username || username === user?.username) {
        setUsernameStatus({ checking: false, available: null, message: '' });
        return;
      }
      
      // Validate format
      const usernameRegex = /^[a-zA-Z0-9_-]+$/;
      if (!usernameRegex.test(username)) {
        setUsernameStatus({ 
          checking: false, 
          available: false, 
          message: 'Only letters, numbers, underscores and hyphens allowed' 
        });
        return;
      }
      
      if (username.length < 3) {
        setUsernameStatus({ 
          checking: false, 
          available: false, 
          message: 'Username must be at least 3 characters' 
        });
        return;
      }
      
      setUsernameStatus({ checking: true, available: null, message: 'Checking...' });
      
      try {
        const response = await axios.post(`${API}/auth/check-username?username=${encodeURIComponent(username)}`);
        setUsernameStatus({ 
          checking: false, 
          available: response.data.available, 
          message: response.data.available ? 'Username available!' : 'Username already taken' 
        });
      } catch (error) {
        setUsernameStatus({ checking: false, available: null, message: '' });
      }
    }, 500),
    [user?.username]
  );

  const handleUsernameChange = (e) => {
    const newUsername = e.target.value.replace(/[^a-zA-Z0-9_-]/g, '');
    setPersonalInfo({ ...personalInfo, username: newUsername });
    checkUsernameAvailability(newUsername);
  };

  const openEditLocation = (location) => {
    setEditingLocation(location);
    setNewLocation({
      label: location.label,
      full_address: location.full_address,
      street: location.street || '',
      barangay: location.barangay || '',
      city: location.city || '',
      province: location.province || '',
      country: location.country || 'Philippines',
      coordinates: (location.lat != null && location.lng != null)
        ? [location.lat, location.lng]
        : (location.coordinates || null),
      is_default: location.is_default
    });
    setShowLocationModal(true);
  };

  const fetchAllSettings = async () => {
    try {
      // Fetch each setting independently to prevent one failure from blocking others
      const results = await Promise.allSettled([
        axios.get(`${API}/settings`),
        axios.get(`${API}/security-settings`),
        axios.get(`${API}/currencies`),
        axios.get(`${API}/payment-methods`)
      ]);

      // Handle settings
      if (results[0].status === 'fulfilled') {
        const settings = results[0].value.data;
        setNotificationSettings({
          email_notifications: settings.email_notifications ?? true,
          sms_notifications: settings.sms_notifications ?? false,
          push_notifications: settings.push_notifications ?? true,
          marketing_emails: settings.marketing_emails ?? true,
          booking_notifications: settings.booking_notifications ?? true,
          message_notifications: settings.message_notifications ?? true,
          push_chat: settings.push_chat ?? true,
          push_bookings: settings.push_bookings ?? true,
          push_payments: settings.push_payments ?? true,
          push_disputes: settings.push_disputes ?? true
        });
        setPrivacySettings({
          profile_visibility: settings.profile_visibility ?? 'public',
          show_email: settings.show_email ?? false,
          show_phone: settings.show_phone ?? false,
          show_location: settings.show_location ?? true,
          allow_reviews: settings.allow_reviews ?? true
        });
        setLanguageSettings({
          language: (settings.language === 'fil' ? 'fil' : 'en'),
          timezone: settings.timezone ?? 'Asia/Manila'
        });
        setAppLanguage(settings.language === 'fil' ? 'fil' : 'en');
      } else {
        // Set defaults if settings fetch fails
        setNotificationSettings({
          email_notifications: true,
          sms_notifications: false,
          push_notifications: true,
          marketing_emails: true,
          booking_notifications: true,
          message_notifications: true,
          push_chat: true,
          push_bookings: true,
          push_payments: true,
          push_disputes: true
        });
        setPrivacySettings({
          profile_visibility: 'public',
          show_email: false,
          show_phone: false,
          show_location: true,
          allow_reviews: true
        });
      }

      // Handle security settings
      if (results[1].status === 'fulfilled') {
        setSecuritySettings(results[1].value.data);
      } else {
        // Set default security settings
        setSecuritySettings({
          two_factor_enabled: false,
          login_alerts: true,
          session_timeout: 30
        });
      }

      // Handle currencies
      if (results[2].status === 'fulfilled') {
        setCurrencies(results[2].value.data.currencies || []);
      } else {
        // Set default currencies
        setCurrencies(['USD', 'EUR', 'GBP', 'PHP', 'JPY', 'AUD', 'CAD', 'SGD']);
      }

      // Handle payment methods
      if (results[3].status === 'fulfilled') {
        setPaymentMethods(results[3].value.data || []);
      }

      // Fetch tax info if exists
      try {
        const taxRes = await axios.get(`${API}/tax-information`);
        if (taxRes.data) {
          setTaxInfo(taxRes.data);
        }
      } catch (e) {
        // Tax info doesn't exist yet, keep defaults
      }

      // Fetch offered services if provider
      if (user?.role === 'provider' || user?.active_role === 'provider') {
        try {
          const servicesRes = await axios.get(`${API}/offered-services`);
          setOfferedServices(servicesRes.data || []);
        } catch (e) {
          console.error('Error fetching services:', e);
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(''), 3000);
  };

  const showError = (message) => {
    setError(message);
    setTimeout(() => setError(''), 5000);
  };

  const handlePersonalInfoUpdate = async () => {
    // Validate username if changed
    if (personalInfo.username !== user?.username) {
      if (usernameStatus.available === false) {
        showError('Please choose a different username');
        return;
      }
      if (usernameStatus.checking) {
        showError('Please wait for username validation');
        return;
      }
    }
    
    setSaving(true);
    try {
      const response = await axios.put(`${API}/auth/profile`, personalInfo);
      onUpdate(response.data);
      showSuccess('Personal information updated successfully!');
      // Reset username status after successful save
      setUsernameStatus({ checking: false, available: null, message: '' });
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to update personal information');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showError('Image size should be less than 5MB');
        return;
      }
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleAvatarUpload = async () => {
    if (!avatarFile) return;
    
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', avatarFile);
      
      const response = await axios.post(`${API}/upload/profile-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Update user with new avatar
      const userResponse = await axios.get(`${API}/auth/me`);
      onUpdate(userResponse.data);
      setAvatarPreview(`${BACKEND_URL}${response.data.avatar_url}`);
      setAvatarFile(null);
      showSuccess('Profile photo updated successfully!');
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to upload profile photo');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      showError('New passwords do not match');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/settings/change-password`, passwordData);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      showSuccess('Password changed successfully!');
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handlePrivacyUpdate = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/privacy`, privacySettings);
      showSuccess('Privacy settings updated!');
    } catch (error) {
      showError('Failed to update privacy settings');
    } finally {
      setSaving(false);
    }
  };

  const handleExportMyData = async () => {
    setExportingData(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/privacy/export-my-data`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `GoodJob_MyData_${user?.id?.substring(0, 8) || 'report'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccess('Your data report has been downloaded!');
    } catch (error) {
      showError('Failed to export data. Please try again.');
    } finally {
      setExportingData(false);
    }
  };

  const handleNotificationUpdate = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/notifications`, notificationSettings);
      showSuccess('Notification settings updated!');
    } catch (error) {
      showError('Failed to update notification settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTaxInfoUpdate = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/tax-information`, taxInfo);
      showSuccess('Tax information updated!');
    } catch (error) {
      showError('Failed to update tax information');
    } finally {
      setSaving(false);
    }
  };

  const handleCurrencyUpdate = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/user/currency?currency=${selectedCurrency}`);
      const userResponse = await axios.get(`${API}/auth/me`);
      onUpdate(userResponse.data);
      showSuccess('Currency updated!');
    } catch (error) {
      showError('Failed to update currency');
    } finally {
      setSaving(false);
    }
  };

  const handleLanguageUpdate = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/language`, languageSettings);
      setAppLanguage(languageSettings.language);
      showSuccess('Language settings updated!');
    } catch (error) {
      showError('Failed to update language settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleTwoFactor = async (enabled) => {
    try {
      await axios.put(`${API}/security-settings/two-factor?enabled=${enabled}`);
      setSecuritySettings({...securitySettings, two_factor_enabled: enabled});
      showSuccess(`Two-factor authentication ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      showError('Failed to update two-factor authentication');
    }
  };

  const toggleLoginAlerts = async (enabled) => {
    try {
      await axios.put(`${API}/security-settings/login-alerts?enabled=${enabled}`);
      setSecuritySettings({...securitySettings, login_alerts: enabled});
      showSuccess(`Login alerts ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      showError('Failed to update login alerts');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl font-semibold text-gray-600">Loading settings...</div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'personal', label: t('settings.tabPersonal'), icon: 'user' },
    { id: 'locations', label: t('settings.tabLocations'), icon: 'map' },
    { id: 'security', label: t('settings.tabSecurity'), icon: 'lock' },
    { id: 'privacy', label: t('settings.tabPrivacy'), icon: 'shield' },
    { id: 'notifications', label: t('settings.tabNotifications'), icon: 'bell' },
    { id: 'taxes', label: t('settings.tabTaxes'), icon: 'file' },
    { id: 'payments', label: t('settings.tabPayments'), icon: 'card' },
    { id: 'language', label: t('settings.tabLanguage'), icon: 'globe' },
  ];

  if (user?.active_role === 'provider') {
    tabs.push({ id: 'credentials', label: t('settings.tabCredentials'), icon: 'award' });
  }

  const getTabIcon = (icon) => {
    const icons = {
      user: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>,
      map: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
      lock: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>,
      shield: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
      bell: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>,
      file: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
      card: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>,
      globe: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
      briefcase: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
      award: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg>,
    };
    return icons[icon] || icons.user;
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="account-settings-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">
          Account Settings
        </h1>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-xl flex items-center gap-2" data-testid="success-message">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {success}
          </div>
        )}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl" data-testid="error-message">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-gray-100 p-4 sticky top-4">
              <nav className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full text-left px-4 py-3 rounded-xl font-medium transition flex items-center gap-3 ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    {getTabIcon(tab.icon)}
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              {/* PERSONAL INFORMATION */}
              {activeTab === 'personal' && (
                <div data-testid="personal-info-section">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('settings.personalInfo')}</h2>
                  <div className="space-y-4">
                    {/* Avatar Upload Section */}
                    <div className="flex items-center gap-6 p-5 bg-gray-50 rounded-xl mb-6 border border-gray-100">
                      <div className="relative">
                        {avatarPreview ? (
                          <img
                            src={avatarPreview}
                            alt="Profile"
                            className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-sm"
                          />
                        ) : (
                          <div className="w-24 h-24 rounded-full bg-gray-900 flex items-center justify-center text-white text-3xl font-bold border-4 border-white shadow-sm">
                            {user?.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{t('settings.profilePhoto')}</h3>
                        <p className="text-sm text-gray-500 mb-3">Upload a photo to personalize your profile. Max 5MB.</p>
                        <div className="flex gap-2">
                          <label className="cursor-pointer bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-full hover:bg-gray-50 font-medium text-sm transition">
                            {avatarFile ? t('settings.changePhoto') : t('settings.choosePhoto')}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleAvatarChange}
                              className="hidden"
                              data-testid="avatar-file-input"
                            />
                          </label>
                          {avatarFile && (
                            <button
                              onClick={handleAvatarUpload}
                              disabled={uploadingAvatar}
                              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-2 rounded-lg hover:shadow-lg font-medium text-sm disabled:opacity-50"
                              data-testid="upload-avatar-btn"
                            >
                              {uploadingAvatar ? 'Uploading...' : 'Upload Photo'}
                            </button>
                          )}
                        </div>
                        {avatarFile && (
                          <p className="text-sm text-green-600 mt-2">✓ {avatarFile.name} selected</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">{t('settings.fullName')}</label>
                        <input
                          type="text"
                          value={personalInfo.name}
                          onChange={(e) => setPersonalInfo({...personalInfo, name: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg"
                          data-testid="name-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">{t('settings.username')}</label>
                        <div className="relative">
                          <input
                            type="text"
                            value={personalInfo.username}
                            onChange={handleUsernameChange}
                            className={`w-full px-3 py-2 border rounded-lg ${
                              usernameStatus.available === false ? 'border-red-500' : 
                              usernameStatus.available === true ? 'border-green-500' : ''
                            }`}
                            placeholder={t('settings.usernamePlaceholder')}
                            data-testid="username-input"
                          />
                          {usernameStatus.checking && (
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                              Checking...
                            </span>
                          )}
                        </div>
                        {usernameStatus.message && (
                          <p className={`text-xs mt-1 ${
                            usernameStatus.available === false ? 'text-red-500' : 
                            usernameStatus.available === true ? 'text-green-500' : 'text-gray-500'
                          }`}>
                            {usernameStatus.message}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">Letters, numbers, underscores and hyphens only</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">{t('settings.email')}</label>
                        <input
                          type="email"
                          value={personalInfo.email}
                          disabled
                          className="w-full px-3 py-2 border rounded-lg bg-gray-100 cursor-not-allowed"
                          data-testid="email-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">{t('settings.phoneNumber')}</label>
                        <input
                          type="tel"
                          value={personalInfo.phone}
                          onChange={(e) => setPersonalInfo({...personalInfo, phone: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg"
                          placeholder={t('settings.phonePlaceholder')}
                          data-testid="phone-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2">{t('settings.location')}</label>
                      <input
                        type="text"
                        value={personalInfo.location}
                        onChange={(e) => setPersonalInfo({...personalInfo, location: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder={t('settings.locationPlaceholder')}
                        data-testid="location-input"
                      />
                    </div>
                    {user?.role === 'provider' && (
                      <div>
                        <label className="block text-sm font-semibold mb-2">{t('settings.skills')}</label>
                        <input
                          type="text"
                          value={personalInfo.skills}
                          onChange={(e) => setPersonalInfo({...personalInfo, skills: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg"
                          placeholder={t('settings.skillsPlaceholder')}
                          data-testid="skills-input"
                        />
                      </div>
                    )}
                    {user?.role === 'provider' && (
                      <div>
                        <label className="block text-sm font-semibold mb-2">{t('settings.bio')}</label>
                        <textarea
                          value={personalInfo.bio}
                          onChange={(e) => setPersonalInfo({...personalInfo, bio: e.target.value})}
                          rows="4"
                          className="w-full px-3 py-2 border rounded-lg"
                          placeholder={t('settings.bioPlaceholder')}
                          data-testid="bio-input"
                        ></textarea>
                      </div>
                    )}
                    {/* ID Documents Section */}
                    {(user?.id_front_image || user?.id_back_image || user?.selfie_with_id) && (
                      <div className="pt-4 border-t" data-testid="id-documents-section">
                        <div className="flex items-center justify-between mb-3">
                          <label className="block text-sm font-semibold">{t('settings.uploadedIdDocs')}</label>
                          {user?.id_verification_status === 'rejected' && (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-semibold">Rejected — Please replace</span>
                          )}
                          {user?.id_verification_status === 'approved' && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-semibold">Verified</span>
                          )}
                          {user?.id_verification_status === 'pending' && (
                            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full font-semibold">Pending Review</span>
                          )}
                        </div>
                        {user?.id_verification_status === 'rejected' && user?.kyc_rejection_reasons?.length > 0 && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 text-sm text-red-700">
                            <strong>Reason:</strong> {(() => {
                              const last = user.kyc_rejection_reasons[user.kyc_rejection_reasons.length - 1];
                              return typeof last === 'string' ? last : (last?.reason || JSON.stringify(last));
                            })()}
                          </div>
                        )}
                        {user?.id_verification_status === 'rejected' && (
                          <div className="mb-3 flex flex-wrap items-center gap-3" data-testid="kyc-retry-row">
                            <button
                              type="button"
                              disabled={saving || !user?.id_front_image || !user?.selfie_with_id}
                              onClick={async () => {
                                try {
                                  setSaving(true);
                                  setError('');
                                  const token = localStorage.getItem('token');
                                  const resp = await axios.post(`${API}/kyc/verify`, {
                                    id_front_image: user.id_front_image,
                                    id_back_image: user.id_back_image,
                                    selfie_with_id: user.selfie_with_id,
                                    police_clearance: user.police_clearance,
                                    id_type: user.id_type,
                                    id_number: user.id_number,
                                  }, { headers: { Authorization: `Bearer ${token}` } });
                                  const meRes = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
                                  if (onUpdate) onUpdate(meRes.data);
                                  if (resp.data?.status === 'approved') {
                                    setSuccess('Verification approved! You are now verified.');
                                  } else if (resp.data?.status === 'rejected') {
                                    setError(`Still rejected: ${resp.data?.message?.message || 'Please replace your photos with clearer ones.'}`);
                                  } else {
                                    setSuccess('Re-submitted. An admin will review your documents shortly.');
                                  }
                                  setTimeout(() => setSuccess(''), 5000);
                                } catch (err) {
                                  setError(err.response?.data?.detail || 'Failed to re-submit verification');
                                } finally {
                                  setSaving(false);
                                }
                              }}
                              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-5 py-2 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50 transition flex items-center gap-2"
                              data-testid="try-again-ai-btn"
                            >
                              {saving ? (
                                <>
                                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
                                  Verifying...
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                  Try Again with AI
                                </>
                              )}
                            </button>
                            <p className="text-xs text-gray-500">Replace any blurry photos above first, then click to re-run AI verification.</p>
                          </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          {[
                            { key: 'id_front_image', label: t('settings.idFront') },
                            { key: 'id_back_image', label: t('settings.idBack') },
                            { key: 'selfie_with_id', label: t('settings.selfieWithId') },
                          ].map(({ key, label }) => (
                            <div key={key} className="text-center" data-testid={`id-doc-${key}`}>
                              <p className="text-xs text-gray-500 mb-1 font-medium">{label}</p>
                              {user?.[key] ? (
                                <div className="relative group">
                                  <img
                                    src={`${BACKEND_URL}${user[key]}`}
                                    alt={label}
                                    className="w-full h-32 object-cover rounded-lg border border-gray-200"
                                  />
                                  <label className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 flex items-center justify-center transition cursor-pointer rounded-lg">
                                    <span className="text-white opacity-0 group-hover:opacity-100 text-xs font-semibold bg-orange-500 px-3 py-1.5 rounded-lg transition">
                                      Replace
                                    </span>
                                    <input
                                      type="file"
                                      accept="image/jpeg,image/png,image/webp"
                                      className="hidden"
                                      data-testid={`replace-${key}`}
                                      onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        if (file.size > 5 * 1024 * 1024) { setError('File must be less than 5MB'); return; }
                                        try {
                                          const { analyzeImageQuality } = await import('../utils/imageQuality');
                                          const expectFace = key === 'selfie_with_id' || key === 'id_front_image';
                                          const q = await analyzeImageQuality(file, { expectFace });
                                          if (!q.ok && q.issues.length > 0) {
                                            const proceed = window.confirm(
                                              `We checked your ${label} and found:\n\n• ${q.issues.join('\n• ')}\n\nQuality score: ${q.score}/100\n\nTap Cancel to retake, or OK to upload anyway.`
                                            );
                                            if (!proceed) { e.target.value = ''; return; }
                                          }
                                        } catch (qErr) {
                                          console.warn('Image quality check failed:', qErr);
                                        }
                                        try {
                                          setSaving(true);
                                          const token = localStorage.getItem('token');
                                          const uploadData = new FormData();
                                          uploadData.append('file', file);
                                          uploadData.append('image_type', key === 'selfie_with_id' ? 'selfie' : key === 'id_front_image' ? 'front' : 'back');
                                          const uploadRes = await axios.post(`${API}/upload/id-image`, uploadData, {
                                            headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
                                          });
                                          // Update user profile with new image URL
                                          await axios.put(`${API}/auth/profile`, { [key]: uploadRes.data.url }, {
                                            headers: { 'Authorization': `Bearer ${token}` }
                                          });
                                          // Refresh user data
                                          const meRes = await axios.get(`${API}/auth/me`, { headers: { 'Authorization': `Bearer ${token}` } });
                                          if (onUpdate) onUpdate(meRes.data);
                                          setSuccess(`${label} replaced successfully`);
                                          setTimeout(() => setSuccess(''), 3000);
                                        } catch (err) {
                                          setError(`Failed to replace ${label}: ${err.response?.data?.detail || err.message}`);
                                        } finally {
                                          setSaving(false);
                                        }
                                      }}
                                    />
                                  </label>
                                </div>
                              ) : (
                                <div className="w-full h-32 bg-gray-100 rounded-lg border border-dashed border-gray-300 flex items-center justify-center">
                                  <label className="cursor-pointer text-center">
                                    <p className="text-gray-400 text-xs">Not uploaded</p>
                                    <p className="text-orange-500 text-xs font-semibold mt-1">Upload</p>
                                    <input
                                      type="file"
                                      accept="image/jpeg,image/png,image/webp"
                                      className="hidden"
                                      data-testid={`upload-${key}`}
                                      onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        if (file.size > 5 * 1024 * 1024) { setError('File must be less than 5MB'); return; }
                                        try {
                                          const { analyzeImageQuality } = await import('../utils/imageQuality');
                                          const expectFace = key === 'selfie_with_id' || key === 'id_front_image';
                                          const q = await analyzeImageQuality(file, { expectFace });
                                          if (!q.ok && q.issues.length > 0) {
                                            const proceed = window.confirm(
                                              `We checked your ${label} and found:\n\n• ${q.issues.join('\n• ')}\n\nQuality score: ${q.score}/100\n\nTap Cancel to retake, or OK to upload anyway.`
                                            );
                                            if (!proceed) { e.target.value = ''; return; }
                                          }
                                        } catch (qErr) {
                                          console.warn('Image quality check failed:', qErr);
                                        }
                                        try {
                                          setSaving(true);
                                          const token = localStorage.getItem('token');
                                          const uploadData = new FormData();
                                          uploadData.append('file', file);
                                          uploadData.append('image_type', key === 'selfie_with_id' ? 'selfie' : key === 'id_front_image' ? 'front' : 'back');
                                          const uploadRes = await axios.post(`${API}/upload/id-image`, uploadData, {
                                            headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
                                          });
                                          await axios.put(`${API}/auth/profile`, { [key]: uploadRes.data.url }, {
                                            headers: { 'Authorization': `Bearer ${token}` }
                                          });
                                          const meRes = await axios.get(`${API}/auth/me`, { headers: { 'Authorization': `Bearer ${token}` } });
                                          if (onUpdate) onUpdate(meRes.data);
                                          setSuccess(`${label} uploaded successfully`);
                                          setTimeout(() => setSuccess(''), 3000);
                                        } catch (err) {
                                          setError(`Failed to upload ${label}: ${err.response?.data?.detail || err.message}`);
                                        } finally {
                                          setSaving(false);
                                        }
                                      }}
                                    />
                                  </label>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-2 pt-4">
                      <div className="flex-1">
                        <p className="text-sm text-gray-600">Account Type: <strong>{user?.role}</strong></p>
                        <p className="text-sm text-gray-600">Member since: <strong>{new Date(user?.created_at).toLocaleDateString()}</strong></p>
                      </div>
                      <button
                        onClick={handlePersonalInfoUpdate}
                        disabled={saving}
                        className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50"
                        data-testid="save-personal-info"
                      >
                        {saving ? t('settings.saving') : t('settings.saveChanges')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* SAVED LOCATIONS */}
              {activeTab === 'locations' && (
                <div data-testid="locations-section">
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h2 className="text-2xl font-bold">{t('settings.savedLocations')}</h2>
                      <p className="text-gray-600 text-sm">Manage your saved addresses for easy job posting and booking</p>
                    </div>
                    <button
                      onClick={() => {
                        setEditingLocation(null);
                        setNewLocation({
                          label: '',
                          full_address: '',
                          street: '',
                          barangay: '',
                          city: '',
                          province: '',
                          country: 'Philippines',
                          coordinates: null,
                          is_default: savedLocations.length === 0
                        });
                        setShowLocationModal(true);
                      }}
                      className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-2 rounded-lg font-semibold hover:shadow-lg"
                      data-testid="add-location-btn"
                    >
                      {t('settings.addLocationBtn')}
                    </button>
                  </div>

                  {savedLocations.length === 0 ? (
                    <div className="bg-gray-50 rounded-lg p-8 text-center">
                      <p className="text-gray-600 mb-4">{t('settings.noSavedLocations')}</p>
                      <p className="text-sm text-gray-500">{t('settings.noSavedLocationsDesc')}</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {savedLocations.map((location) => (
                        <div
                          key={location.id}
                          className="border rounded-lg p-4 hover:shadow-md transition"
                          data-testid={`location-${location.id}`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-bold text-gray-800">📍 {location.label}</h3>
                                {location.is_default && (
                                  <span className="bg-blue-100 text-blue-600 text-xs px-2 py-1 rounded-full">{t('settings.default')}</span>
                                )}
                              </div>
                              <p className="text-gray-600">{location.full_address}</p>
                              {location.street && (
                                <div className="text-sm text-gray-500 mt-2 grid grid-cols-2 gap-2">
                                  {location.street && <span>{t('settings.streetColon')} {location.street}</span>}
                                  {location.barangay && <span>{t('settings.barangayColon')} {location.barangay}</span>}
                                  {location.city && <span>{t('settings.cityColon')} {location.city}</span>}
                                  {location.province && <span>{t('settings.provinceColon')} {location.province}</span>}
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => openEditLocation(location)}
                                className="text-blue-600 hover:text-blue-800 font-medium"
                                data-testid={`edit-location-${location.id}`}
                              >
                                {t('settings.edit')}
                              </button>
                              <button
                                onClick={() => handleDeleteLocation(location.id)}
                                className="text-red-500 hover:text-red-700 font-medium"
                                data-testid={`delete-location-${location.id}`}
                              >
                                {t('settings.delete')}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* LOGIN & SECURITY */}
              {activeTab === 'security' && securitySettings && (
                <div data-testid="security-section">
                  <h2 className="text-2xl font-bold mb-6">{t('settings.loginSecurity')}</h2>
                  
                  {/* Change Password */}
                  <div className="mb-8 pb-8 border-b">
                    <h3 className="text-lg font-bold mb-4">{t('settings.changePassword')}</h3>
                    <div className="space-y-4 max-w-md">
                      <div>
                        <label className="block text-sm font-semibold mb-2">{t('settings.currentPassword')}</label>
                        <div className="relative">
                          <input
                            type={showCurrentPw ? 'text' : 'password'}
                            value={passwordData.current_password}
                            onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                            className="w-full px-3 py-2 border rounded-lg pr-10"
                            data-testid="current-password-input"
                          />
                          <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="toggle-current-pw">
                            {showCurrentPw ? (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" /></svg>
                            ) : (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                            )}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">{t('settings.newPassword')}</label>
                        <div className="relative">
                          <input
                            type={showNewPw ? 'text' : 'password'}
                            value={passwordData.new_password}
                            onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                            className="w-full px-3 py-2 border rounded-lg pr-10"
                            data-testid="new-password-input"
                          />
                          <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="toggle-new-pw">
                            {showNewPw ? (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" /></svg>
                            ) : (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                            )}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">{t('settings.confirmNewPassword')}</label>
                        <div className="relative">
                          <input
                            type={showConfirmPw ? 'text' : 'password'}
                            value={passwordData.confirm_password}
                            onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                            className="w-full px-3 py-2 border rounded-lg pr-10"
                            data-testid="confirm-password-input"
                          />
                          <button type="button" onClick={() => setShowConfirmPw(!showConfirmPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="toggle-confirm-pw">
                            {showConfirmPw ? (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" /></svg>
                            ) : (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                            )}
                          </button>
                        </div>
                      </div>
                      <button
                        onClick={handlePasswordChange}
                        disabled={saving}
                        className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50"
                        data-testid="change-password-button"
                      >
                        {saving ? t('settings.changingPassword') : t('settings.changePassword')}
                      </button>
                      {securitySettings.last_password_change && (
                        <p className="text-sm text-gray-500">
                          {t('settings.lastChanged')} {new Date(securitySettings.last_password_change).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* OTP Verification (only shown if globally enabled by admin) */}
                  {otpGlobalEnabled && (
                    <div className="mb-8 pb-8 border-b">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-lg font-bold mb-2">{t('settings.otpVerification')}</h3>
                          <p className="text-sm text-gray-600 mb-2">
                            {t('settings.otpDesc1')} <strong>{t('settings.otpDescBold')}</strong> {t('settings.otpDesc2')}
                          </p>
                          {userOtpEnabled ? (
                            <p className="text-xs text-green-600 font-medium">{t('settings.otpEnabledMsg')}</p>
                          ) : (
                            <p className="text-xs text-gray-500">{t('settings.otpDisabledMsg')}</p>
                          )}
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={userOtpEnabled}
                            onChange={(e) => toggleUserOtp(e.target.checked)}
                            className="sr-only peer"
                            data-testid="user-otp-toggle"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* PRIVACY */}
              {activeTab === 'privacy' && privacySettings && (
                <div data-testid="privacy-section">
                  <h2 className="text-2xl font-bold mb-6">{t('settings.privacySettings')}</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold mb-2">{t('settings.profileVisibility')}</label>
                      <select
                        value={privacySettings.profile_visibility}
                        onChange={(e) => setPrivacySettings({...privacySettings, profile_visibility: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        data-testid="profile-visibility-select"
                      >
                        <option value="public">{t('settings.visPublic')}</option>
                        <option value="private">{t('settings.visPrivate')}</option>
                        <option value="providers_only">{t('settings.visProviders')}</option>
                        <option value="clients_only">{t('settings.visClients')}</option>
                      </select>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center justify-between py-3 border-b">
                        <div>
                          <p className="font-semibold">{t('settings.showLocation')}</p>
                          <p className="text-sm text-gray-600">{t('settings.showLocationDesc')}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={privacySettings.show_location}
                            onChange={(e) => setPrivacySettings({...privacySettings, show_location: e.target.checked})}
                            className="sr-only peer"
                            data-testid="show-location-toggle"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between py-3">
                        <div>
                          <p className="font-semibold">{t('settings.allowReviews')}</p>
                          <p className="text-sm text-gray-600">{t('settings.allowReviewsDesc')}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={privacySettings.allow_reviews}
                            onChange={(e) => setPrivacySettings({...privacySettings, allow_reviews: e.target.checked})}
                            className="sr-only peer"
                            data-testid="allow-reviews-toggle"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    </div>

                    <button
                      onClick={handlePrivacyUpdate}
                      disabled={saving}
                      className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50"
                      data-testid="save-privacy-settings"
                    >
                      {saving ? t('settings.saving') : t('settings.savePrivacy')}
                    </button>

                    {/* Data Rights Section */}
                    <div className="mt-10 pt-8 border-t-2 border-gray-200">
                      <h3 className="text-xl font-bold mb-2">{t('settings.yourDataRights')}</h3>
                      <p className="text-sm text-gray-500 mb-6">
                        {t('settings.dataRightsDesc')}
                      </p>

                      {/* Request My Data */}
                      <div className="bg-blue-50 rounded-xl p-5 border border-blue-200 mb-4">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              {t('settings.requestMyData')}
                            </h4>
                            <p className="text-sm text-gray-600 mt-1">
                              {t('settings.requestMyDataDesc')}
                            </p>
                          </div>
                          <button
                            onClick={handleExportMyData}
                            disabled={exportingData}
                            className="flex-shrink-0 bg-blue-600 text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50 transition flex items-center gap-2"
                            data-testid="request-my-data-btn"
                          >
                            {exportingData ? (
                              <>
                                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                {t('settings.generating')}
                              </>
                            ) : t('settings.downloadMyData')}
                          </button>
                        </div>
                      </div>

                      {/* Delete My Account */}
                      <div className="bg-red-50 rounded-xl p-5 border border-red-200">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h4 className="font-semibold text-red-800 flex items-center gap-2">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              {t('settings.deleteMyAccountData')}
                            </h4>
                            <p className="text-sm text-gray-600 mt-1">
                              {t('settings.deleteAccountDesc')}
                            </p>
                          </div>
                          <button
                            onClick={() => setShowDeleteModal(true)}
                            className="flex-shrink-0 bg-red-600 text-white px-5 py-2.5 rounded-lg hover:bg-red-700 font-semibold transition"
                            data-testid="delete-account-btn"
                          >
                            {t('settings.deleteAccount')}
                          </button>
                        </div>
                      </div>

                      {/* Privacy Email Notice */}
                      <p className="text-xs text-gray-500 mt-6 pt-4 border-t border-gray-200">
                        You may request access or deletion of your data by emailing{' '}
                        {privacyEmail ? (
                          <a href={`mailto:${privacyEmail}`} className="text-blue-600 font-semibold underline">{privacyEmail}</a>
                        ) : (
                          <span className="text-gray-400 italic">privacy email not yet configured</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Delete Account flow (OTP + eligibility + grace period) */}
              {showDeleteModal && (
                <DeleteAccountModal
                  onClose={() => setShowDeleteModal(false)}
                  onDeleted={() => { setShowDeleteModal(false); if (onLogout) onLogout(); }}
                />
              )}

              {/* NOTIFICATIONS */}
              {activeTab === 'notifications' && notificationSettings && (
                <div data-testid="notifications-section">
                  <h2 className="text-2xl font-bold mb-6">{t('settings.notificationPreferences')}</h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b">
                      <div>
                        <p className="font-semibold">{t('settings.emailNotif')}</p>
                        <p className="text-sm text-gray-600">{t('settings.emailNotifDesc')}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationSettings.email_notifications}
                          onChange={(e) => setNotificationSettings({...notificationSettings, email_notifications: e.target.checked})}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between py-3 border-b">
                      <div>
                        <p className="font-semibold">{t('settings.smsNotif')}</p>
                        <p className="text-sm text-gray-600">{t('settings.smsNotifDesc')}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationSettings.sms_notifications}
                          onChange={(e) => setNotificationSettings({...notificationSettings, sms_notifications: e.target.checked})}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between py-3 border-b">
                      <div>
                        <p className="font-semibold">{t('settings.pushNotif')}</p>
                        <p className="text-sm text-gray-600">{t('settings.pushNotifDesc')}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationSettings.push_notifications}
                          onChange={(e) => setNotificationSettings({...notificationSettings, push_notifications: e.target.checked})}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between py-3 border-b">
                      <div>
                        <p className="font-semibold">{t('settings.marketingEmails')}</p>
                        <p className="text-sm text-gray-600">{t('settings.marketingDesc')}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationSettings.marketing_emails}
                          onChange={(e) => setNotificationSettings({...notificationSettings, marketing_emails: e.target.checked})}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between py-3 border-b">
                      <div>
                        <p className="font-semibold">{t('settings.bookingNotif')}</p>
                        <p className="text-sm text-gray-600">{t('settings.bookingNotifDesc')}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationSettings.booking_notifications}
                          onChange={(e) => setNotificationSettings({...notificationSettings, booking_notifications: e.target.checked})}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between py-3">
                      <div>
                        <p className="font-semibold">{t('settings.messageNotif')}</p>
                        <p className="text-sm text-gray-600">{t('settings.messageNotifDesc')}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationSettings.message_notifications}
                          onChange={(e) => setNotificationSettings({...notificationSettings, message_notifications: e.target.checked})}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="pt-6 mt-2 border-t" data-testid="push-categories-section">
                      <h3 className="text-lg font-bold mb-1">{t('settings.pushCategories')}</h3>
                      <p className="text-sm text-gray-600 mb-4">{t('settings.pushCategoriesDesc')}</p>

                      <div className="flex items-center justify-between py-3 border-b">
                        <div>
                          <p className="font-semibold">{t('settings.chatMessages')}</p>
                          <p className="text-sm text-gray-600">{t('settings.chatMessagesDesc')}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notificationSettings.push_chat}
                            onChange={(e) => setNotificationSettings({...notificationSettings, push_chat: e.target.checked})}
                            className="sr-only peer"
                            data-testid="push-chat-toggle"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between py-3 border-b">
                        <div>
                          <p className="font-semibold">{t('settings.bookingsJobs')}</p>
                          <p className="text-sm text-gray-600">{t('settings.bookingsJobsDesc')}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notificationSettings.push_bookings}
                            onChange={(e) => setNotificationSettings({...notificationSettings, push_bookings: e.target.checked})}
                            className="sr-only peer"
                            data-testid="push-bookings-toggle"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between py-3 border-b">
                        <div>
                          <p className="font-semibold">{t('settings.paymentsEarnings')}</p>
                          <p className="text-sm text-gray-600">{t('settings.paymentsEarningsDesc')}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notificationSettings.push_payments}
                            onChange={(e) => setNotificationSettings({...notificationSettings, push_payments: e.target.checked})}
                            className="sr-only peer"
                            data-testid="push-payments-toggle"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between py-3">
                        <div>
                          <p className="font-semibold">{t('settings.disputes')}</p>
                          <p className="text-sm text-gray-600">{t('settings.disputesDesc')}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notificationSettings.push_disputes}
                            onChange={(e) => setNotificationSettings({...notificationSettings, push_disputes: e.target.checked})}
                            className="sr-only peer"
                            data-testid="push-disputes-toggle"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    </div>

                    <button
                      onClick={handleNotificationUpdate}
                      disabled={saving}
                      className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50 mt-4"
                      data-testid="save-notification-settings"
                    >
                      {saving ? t('settings.saving') : t('settings.saveNotif')}
                    </button>
                  </div>
                </div>
              )}

              {/* TAXES */}
              {activeTab === 'taxes' && (
                <div data-testid="taxes-section">
                  <h2 className="text-2xl font-bold mb-6">{t('settings.taxInfo')}</h2>
                  <p className="text-gray-600 mb-6">{t('settings.taxInfoDesc')}</p>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">{t('settings.taxIdType')}</label>
                        <select
                          value={taxInfo.tax_id_type}
                          onChange={(e) => setTaxInfo({...taxInfo, tax_id_type: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg"
                          data-testid="tax-id-type-select"
                        >
                          <option value="TIN">TIN (Tax Identification Number)</option>
                          <option value="SSS">SSS Number</option>
                          <option value="VAT">VAT Registration</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">{t('settings.taxIdNumber')}</label>
                        <input
                          type="text"
                          value={taxInfo.tax_id_number}
                          onChange={(e) => setTaxInfo({...taxInfo, tax_id_number: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg"
                          placeholder="XXX-XXX-XXX"
                          data-testid="tax-id-number-input"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">{t('settings.taxClassification')}</label>
                      <select
                        value={taxInfo.tax_classification}
                        onChange={(e) => setTaxInfo({...taxInfo, tax_classification: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        data-testid="tax-classification-select"
                      >
                        <option value="individual">{t('settings.classIndividual')}</option>
                        <option value="business">{t('settings.classBusiness')}</option>
                      </select>
                    </div>

                    {taxInfo.tax_classification === 'business' && (
                      <>
                        <div>
                          <label className="block text-sm font-semibold mb-2">{t('settings.businessName')}</label>
                          <input
                            type="text"
                            value={taxInfo.business_name}
                            onChange={(e) => setTaxInfo({...taxInfo, business_name: e.target.value})}
                            className="w-full px-3 py-2 border rounded-lg"
                            data-testid="business-name-input"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold mb-2">{t('settings.businessAddress')}</label>
                          <textarea
                            value={taxInfo.business_address}
                            onChange={(e) => setTaxInfo({...taxInfo, business_address: e.target.value})}
                            rows="3"
                            className="w-full px-3 py-2 border rounded-lg"
                            data-testid="business-address-input"
                          ></textarea>
                        </div>
                      </>
                    )}

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="vat_registered"
                        checked={taxInfo.vat_registered}
                        onChange={(e) => setTaxInfo({...taxInfo, vat_registered: e.target.checked})}
                        className="mr-2"
                        data-testid="vat-registered-checkbox"
                      />
                      <label htmlFor="vat_registered">{t('settings.vatRegistered')}</label>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-800">
                        <strong>{t('settings.note')}</strong> {t('settings.taxNote')}
                      </p>
                    </div>

                    <button
                      onClick={handleTaxInfoUpdate}
                      disabled={saving}
                      className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50"
                      data-testid="save-tax-info"
                    >
                      {saving ? t('settings.saving') : t('settings.saveTax')}
                    </button>
                  </div>
                </div>
              )}

              {/* PAYMENTS */}
              {activeTab === 'payments' && (
                <div data-testid="payments-section">
                  <h2 className="text-2xl font-bold mb-6">{t('settings.paymentMethods')}</h2>
                  <p className="text-gray-600 mb-6">{t('settings.paymentMethodsDesc')}</p>
                  
                  {paymentMethods.length === 0 ? (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                      <p className="text-gray-600 mb-4">{t('settings.noPaymentMethods')}</p>
                      <a
                        href="/payment-methods"
                        className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-lg hover:shadow-lg font-semibold"
                      >
                        {t('settings.addPaymentMethod')}
                      </a>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {paymentMethods.map((method) => (
                        <div key={method.id} className="border rounded-lg p-4 flex justify-between items-center">
                          <div className="flex items-center gap-3">
                            <img 
                              src={PAYMENT_ICONS[method.type] || PAYMENT_ICONS.bank_account} 
                              alt={method.type} 
                              className="w-10 h-10 object-contain rounded"
                            />
                            <div>
                              <p className="font-bold">
                                {method.type === 'gcash' ? 'GCash' :
                                 method.type === 'maya' ? 'Maya' :
                                 'Bank Transfer'}
                              </p>
                              <p className="text-sm text-gray-600">{method.account_name || method.name}</p>
                              <p className="text-sm text-gray-600">{method.account_number}</p>
                              {method.is_default && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded mt-2 inline-block">Default</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      <a
                        href="/payment-methods"
                        className="inline-block text-blue-600 hover:underline font-semibold"
                      >
                        Manage Payment Methods →
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* LANGUAGE & CURRENCY */}
              {activeTab === 'language' && (
                <div data-testid="language-currency-section">
                  <h2 className="text-2xl font-bold mb-6">{t('settings.languageCurrency')}</h2>
                  
                  <div className="space-y-6">
                    {/* Language Settings */}
                    <div className="pb-6 border-b">
                      <h3 className="text-lg font-bold mb-4">{t('settings.languagePreferences')}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold mb-2">{t('settings.displayLanguage')}</label>
                          <select
                            value={languageSettings.language}
                            onChange={(e) => setLanguageSettings({...languageSettings, language: e.target.value})}
                            className="w-full px-3 py-2 border rounded-lg"
                            data-testid="language-select"
                          >
                            <option value="en">English</option>
                            <option value="fil">Filipino</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold mb-2">{t('settings.timezone')}</label>
                          <select
                            value={languageSettings.timezone}
                            onChange={(e) => setLanguageSettings({...languageSettings, timezone: e.target.value})}
                            className="w-full px-3 py-2 border rounded-lg"
                            data-testid="timezone-select"
                          >
                            <option value="Asia/Manila">Asia/Manila (GMT+8)</option>
                            <option value="America/New_York">America/New York (EST)</option>
                            <option value="Europe/London">Europe/London (GMT)</option>
                            <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                          </select>
                        </div>
                      </div>
                      <button
                        onClick={handleLanguageUpdate}
                        disabled={saving}
                        className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50 mt-4"
                        data-testid="save-language-settings"
                      >
                        {saving ? t('settings.saving') : t('settings.saveLanguage')}
                      </button>
                    </div>

                    {/* Currency Settings */}
                    <div>
                      <h3 className="text-lg font-bold mb-4">{t('settings.currencyPreferences')}</h3>
                      <div>
                        <label className="block text-sm font-semibold mb-2">{t('settings.preferredCurrency')}</label>
                        <select
                          value={selectedCurrency}
                          onChange={(e) => setSelectedCurrency(e.target.value)}
                          className="w-full md:w-1/2 px-3 py-2 border rounded-lg"
                          data-testid="currency-select"
                        >
                          {currencies.map((currency) => (
                            <option key={currency} value={currency}>
                              {currency}
                            </option>
                          ))}
                        </select>
                        <p className="text-sm text-gray-600 mt-2">
                          This currency will be used for displaying prices and your earnings/payments.
                        </p>
                      </div>
                      <button
                        onClick={handleCurrencyUpdate}
                        disabled={saving || selectedCurrency === user?.currency}
                        className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50 mt-4"
                        data-testid="save-currency-settings"
                      >
                        {saving ? t('settings.saving') : t('settings.saveCurrency')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* CREDENTIALS (Provider Only) */}
              {activeTab === 'credentials' && (
                <div data-testid="credentials-section">
                  <h2 className="text-2xl font-bold mb-2">{t('settings.credentials')}</h2>
                  <p className="text-gray-600 mb-6">Upload your clearances and certifications to build trust with clients. These will be visible on your public profile.</p>

                  {/* Credential Type Upload Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                    {[
                      { type: 'barangay_clearance', label: t('settings.credBarangay'), desc: t('settings.credBarangayDesc') },
                      { type: 'police_clearance', label: t('settings.credPolice'), desc: t('settings.credPoliceDesc') },
                      { type: 'nbi_clearance', label: t('settings.credNbi'), desc: t('settings.credNbiDesc') },
                      { type: 'certification', label: t('settings.credCert'), desc: t('settings.credCertDesc') },
                      { type: 'other', label: t('settings.credOther'), desc: t('settings.credOtherDesc') },
                    ].map(item => {
                      const existing = credentials.filter(c => c.type === item.type);
                      return (
                        <div key={item.type} className="border rounded-lg p-4 bg-white" data-testid={`credential-card-${item.type}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="font-semibold text-gray-800">{item.label}</h3>
                              <p className="text-xs text-gray-500">{item.desc}</p>
                            </div>
                            {existing.length > 0 && (
                              <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-medium">
                                {existing.length} uploaded
                              </span>
                            )}
                          </div>

                          {/* Show existing uploads */}
                          {existing.length > 0 && (
                            <div className="grid grid-cols-3 gap-2 mb-3">
                              {existing.map(cred => (
                                <div key={cred.id} className="relative group">
                                  <img
                                    src={cred.image_url.startsWith('http') ? cred.image_url : `${process.env.REACT_APP_BACKEND_URL}${cred.image_url}`}
                                    alt={item.label}
                                    className="w-full h-20 object-cover rounded border cursor-pointer"
                                    onClick={() => window.open(cred.image_url.startsWith('http') ? cred.image_url : `${process.env.REACT_APP_BACKEND_URL}${cred.image_url}`, '_blank')}
                                  />
                                  <button
                                    onClick={() => handleDeleteCredential(cred.id)}
                                    className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition"
                                  >
                                    x
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-blue-600 hover:text-blue-800 font-medium">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            {uploadingCredential ? 'Uploading...' : 'Upload Image'}
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleUploadCredential(e, item.type)}
                              disabled={uploadingCredential}
                              data-testid={`upload-${item.type}`}
                            />
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Location Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col max-h-[90vh]">
            <div className="px-6 py-4 border-b shrink-0">
              <h2 className="text-xl font-bold">{editingLocation ? t('settings.editLocation') : t('settings.addNewLocation')}</h2>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-semibold mb-2">{t('settings.labelReq')}</label>
                <input
                  type="text"
                  value={newLocation.label}
                  onChange={(e) => setNewLocation({ ...newLocation, label: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder={t('settings.labelPlaceholder')}
                  data-testid="location-label-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">📍 Pin Location on Map <span className="text-red-500">*</span></label>
                <LocationPicker
                  initialLocation={newLocation.coordinates}
                  placeholder="Tap to drop a pin on the map"
                  onLocationSelect={(loc) => {
                    if (!loc) {
                      setNewLocation((prev) => ({ ...prev, coordinates: null }));
                      return;
                    }
                    const parts = loc.addressParts || {};
                    setNewLocation((prev) => ({
                      ...prev,
                      coordinates: loc.coordinates,
                      street: parts.street || prev.street,
                      barangay: parts.barangay || prev.barangay,
                      city: parts.city || prev.city,
                      province: parts.province || prev.province,
                      country: parts.country || prev.country || 'Philippines',
                      full_address: parts.full_address || loc.name || prev.full_address,
                    }));
                  }}
                />
                {newLocation.coordinates && (
                  <p className="text-xs text-green-600 mt-1" data-testid="location-pin-confirmed">
                    Pin set: {Number(newLocation.coordinates[0]).toFixed(4)}, {Number(newLocation.coordinates[1]).toFixed(4)} — adjust the address fields below if needed.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">{t('settings.streetLabel')}</label>
                <input
                  type="text"
                  value={newLocation.street}
                  onChange={(e) => {
                    const street = e.target.value;
                    setNewLocation((prev) => ({
                      ...prev,
                      street,
                      full_address: [street, prev.barangay, prev.city, prev.province, prev.country].filter(Boolean).join(', '),
                    }));
                  }}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder={t('settings.streetPlaceholder')}
                  data-testid="location-street-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">{t('settings.provinceCity')}</label>
                <PHAddressSelect
                  province={newLocation.province}
                  city={newLocation.city}
                  barangay={newLocation.barangay}
                  onChange={({ province, city, barangay }) =>
                    setNewLocation((prev) => ({
                      ...prev,
                      province,
                      city,
                      barangay,
                      full_address: [prev.street, barangay, city, province, prev.country].filter(Boolean).join(', '),
                    }))
                  }
                  idPrefix="location"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">{t('settings.country')}</label>
                <input
                  type="text"
                  value={newLocation.country}
                  onChange={(e) => setNewLocation({ ...newLocation, country: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder={t('settings.countryPlaceholder')}
                  data-testid="location-country-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">{t('settings.fullAddress')}</label>
                <input
                  type="text"
                  value={newLocation.full_address}
                  onChange={(e) => setNewLocation({ ...newLocation, full_address: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder={t('settings.fullAddress')}
                  data-testid="location-full-address-input"
                />
                <p className="text-xs text-gray-400 mt-1">Auto-filled from the fields above — edit to add the exact address (house/unit no., landmark).</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_default"
                  checked={newLocation.is_default}
                  onChange={(e) => setNewLocation({ ...newLocation, is_default: e.target.checked })}
                  className="w-4 h-4"
                  data-testid="location-default-checkbox"
                />
                <label htmlFor="is_default" className="text-sm">{t('settings.setDefaultLocation')}</label>
              </div>
            </div>
            <div className="px-6 py-4 border-t flex gap-4 shrink-0">
              <button
                onClick={() => {
                  setShowLocationModal(false);
                  setEditingLocation(null);
                }}
                className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 font-semibold"
                data-testid="cancel-location-btn"
              >
                {t('settings.cancel')}
              </button>
              <button
                onClick={() => {
                  if (!newLocation.coordinates) {
                    setError('Please drop a pin on the map to set the location.');
                    return;
                  }
                  // Use the (editable) full address; fall back to a computed join if empty.
                  const computed = [newLocation.street, newLocation.barangay, newLocation.city, newLocation.province, newLocation.country].filter(Boolean).join(', ');
                  const fullAddr = (newLocation.full_address && newLocation.full_address.trim()) ? newLocation.full_address : computed;
                  setNewLocation(prev => ({ ...prev, full_address: fullAddr }));
                  setTimeout(() => handleSaveLocation(), 100);
                }}
                disabled={!newLocation.coordinates}
                className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-2 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="save-location-btn"
              >
                {editingLocation ? t('settings.updateLocation') : t('settings.addLocationSave')}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AccountSettings;
